import { useEffect, useState } from "react";
import { Loader2, Tag, X } from "lucide-react";
import { api, euro, errMsg } from "@/lib/api";
import { referralCode } from "@/components/site/WelcomePopup";
import { useFlashSale } from "@/lib/flash";
import { useLang } from "@/lib/i18n";

export function PromoField({ items, promo, setPromo, email }) {
  const [code, setCode] = useState(() => referralCode());
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const flash = useFlashSale();
  const { t, lang } = useLang();
  const en = lang === "en";
  const flashCode = flash?.code;
  useEffect(() => { if (flashCode && !promo) setCode((c) => c || flashCode); }, [flashCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = async (e) => {
    e.preventDefault();
    if (!code.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/promo/validate", { code: code.trim(), items, email: /\S+@\S+\.\S+/.test(email || "") ? email.trim() : null });
      setPromo({ code: data?.code, discount: Number(data?.discount ?? 0), label: data?.kind === "percent" ? `-${data?.value} %` : `-${euro(data?.value ?? 0)}` });
      setCode("");
    } catch (err) {
      setError(t(errMsg(err)));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { if (promo && /\S+@\S+\.\S+/.test(email || "")) { api.post("/promo/validate", { code: promo.code, items, email: email.trim() }).catch((err) => { setPromo(null); setError(t(errMsg(err))); }); } }, [email]); // eslint-disable-line react-hooks/exhaustive-deps

  if (promo) {
    return (
      <div data-testid="promo-applied" className="flex items-center justify-between gap-3 rounded-sm border border-[var(--gold)]/50 bg-[var(--gold)]/10 px-4 py-3 text-sm">
        <span className="flex items-center gap-2"><Tag className="h-4 w-4 text-[var(--gold)]" /> Code <strong>{promo.code}</strong> {t("appliqué")} ({promo.label})</span>
        <button type="button" data-testid="promo-remove-button" onClick={() => setPromo(null)} aria-label={t("Retirer le code promo")} className="text-[var(--parchment)]/70 hover:text-[var(--gold-soft)]"><X className="h-4 w-4" /></button>
      </div>
    );
  }
  return (
    <form onSubmit={apply} data-testid="promo-form">
      <label htmlFor="promo-input" className="mb-2 block text-xs uppercase tracking-[0.18em] text-[var(--parchment)]/80">{t("Vous avez un code promo ?")}</label>
      <div className="flex gap-2">
        <input id="promo-input" data-testid="promo-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder={t("Votre code")} autoComplete="off"
          className="h-12 min-w-0 flex-1 rounded-full border border-[var(--line)] bg-transparent px-5 text-sm uppercase tracking-wider outline-none transition-colors focus:border-[var(--gold)]" />
        <button type="submit" data-testid="promo-apply-button" disabled={busy || !code.trim()}
          className="flex h-12 shrink-0 items-center rounded-full border border-[var(--gold)] px-5 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--gold-soft)] transition-colors hover:bg-[var(--gold)] hover:text-[var(--ink)] disabled:opacity-40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Appliquer")}
        </button>
      </div>
      {error ? <p data-testid="promo-error" className="mt-2 text-xs text-red-300">{error}</p> : null}
      {code && code === referralCode() ? <p data-testid="promo-referral-hint" className="mt-2 text-xs text-[var(--gold-soft)]">{en ? "Referral code detected: click “Apply” to get €5 off (from €25)." : "Code de parrainage détecté : cliquez sur « Appliquer » pour vos 5 € offerts (dès 25 €)."}</p> : null}
      {flash && code === flash.code ? <p data-testid="promo-flash-hint" className="mt-2 text-xs font-semibold text-[#f48fb1]">{en ? `Flash sale: click “Apply” for ${flash.percent}% off your order.` : `Vente flash : cliquez sur « Appliquer » pour -${flash.percent} % sur votre commande.`}</p> : null}
    </form>
  );
}
